import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { DivPropsWithoutRef } from '../../common/types';
import { Icon } from '../Icon/Icon';
import { IconSize } from '../Icon/iconSize';
import { ClickableIcon } from '../ClickableIcon';
import { IconActionArrowDropdownBottom, IconActionDelete } from '../../icons/index';
import { SelectSize } from './Select';
import { colorGrayDusk } from '@hse-design/tokens';
import { computeVisibleTags } from '@hse-design/core';
import { SelectTag } from './SelectTag';
import composeRefs from '@seznam/compose-react-refs/composeRefs';
import { ISelectOption } from './types';

export interface SelectValueContainerProps
  extends Omit<DivPropsWithoutRef, 'size'> {
  classes?: Record<
    'value' | 'clearIcon' | 'valuePlaceholder',
    string | undefined
  >;
  showClearIcon?: boolean;
  focused?: boolean;
  disabled?: boolean;
  selected?: boolean;
  leftIcon?: React.ElementType;
  showTags?: boolean;
  focusedTagI: number;
  selectedOptions: ISelectOption[];
  renderSearchInput: (props: { placeholder: string }) => JSX.Element | null;
  clearButtonFocused?: boolean;
  onClearClick?: () => void;
  onClearFocus?: (e: React.FocusEvent<HTMLButtonElement>) => void;
  onClearBlur?: () => void;
  isOpen?: boolean;
  size?: SelectSize;
  multipleValuesRef?: React.Ref<HTMLDivElement>;
  showInputInValueField: boolean;
  showSelectedValueInField: boolean;
  showPlaceholderInField: boolean;
  singleValueLabel: string | null;
  placeholder: string;
}

export const SelectValueContainer = React.forwardRef<
  HTMLDivElement,
  SelectValueContainerProps
>(
  (
    {
      className,
      classes,
      size,
      focused,
      disabled,
      selected,
      children,
      leftIcon,
      showTags,
      selectedOptions,
      multipleValuesRef: multipleValuesRefProp,
      focusedTagI,
      renderSearchInput,
      showClearIcon,
      clearButtonFocused,
      onClearClick,
      isOpen,
      onClearFocus,
      onClearBlur,
      showInputInValueField,
      showSelectedValueInField,
      showPlaceholderInField,
      singleValueLabel,
      placeholder,
      ...props
    }: SelectValueContainerProps,
    ref
  ) => {
    const tagsRightGap = 11;
    const [tagsCount, setTagsCount] = useState(Infinity);
    const multipleValuesRef = useRef<HTMLDivElement>(null);

    const resetVisibleTags = useCallback(() => {
      setTagsCount(Infinity);
    }, []);

    useLayoutEffect(() => {
      resetVisibleTags();
    }, [selectedOptions, resetVisibleTags]);

    useLayoutEffect(() => {
      if (tagsCount === Infinity) {
        const newCount = computeVisibleTags(
          multipleValuesRef.current,
          tagsRightGap
        );
        if (newCount !== Infinity) {
          setTagsCount(newCount);
        }
      }
    });

    const renderTags = () => {
      if (!Array.isArray(selectedOptions)) return null;
      const isNotAllTagsVisible = selectedOptions.length > tagsCount;

      return (
        <>
          <div
            className="hse-Select__multipleValues"
            ref={composeRefs(multipleValuesRef, multipleValuesRefProp)}
          >
            {selectedOptions.slice(0, tagsCount).map((option, optionI) => {
              const label = option.label || option.value;
              return (
                <SelectTag key={optionI} focused={focusedTagI === optionI}>
                  {label}
                </SelectTag>
              );
            })}
          </div>
          {isNotAllTagsVisible && (
            <div className="hse-Select__multipleValuesEtc">...</div>
          )}
          <div
            className={clsx('hse-Select__valuesCounter', {
              'hse-Select__valuesCounter_disabled': disabled
            })}
          >
            {selectedOptions.length}
          </div>
        </>
      );
    };

    return (
      <div
        ref={ref}
        className={clsx('hse-Select__valueContainer', className, {
          'hse-Select__valueContainer_focused': focused,
          'hse-Select__valueContainer_disabled': disabled,
          [`hse-Select__valueContainer_size_${size}`]: size,
          [`hse-Select__valueContainer_selected`]: selected
        })}
        {...props}
      >
        {leftIcon && (
          <Icon
            icon={leftIcon}
            className="hse-Select__valueContainerLeftIcon"
            size={IconSize.medium}
            color={colorGrayDusk}
          />
        )}
        {showTags ? (
          renderTags()
        ) : showInputInValueField ? (
          renderSearchInput({
            placeholder
          })
        ) : showSelectedValueInField ? (
          <div className={clsx('hse-Select__value', classes?.value)}>
            {singleValueLabel}
          </div>
        ) : showPlaceholderInField ? (
          <div
            className={clsx(
              'hse-Select__valuePlaceholder',
              classes?.valuePlaceholder
            )}
          >
            {placeholder}
          </div>
        ) : null}
        {showClearIcon && (
          <ClickableIcon
            disabled={disabled}
            className={clsx('hse-Select__clearIcon', classes?.clearIcon)}
            focused={clearButtonFocused}
            iconProps={{ icon: IconActionDelete, size: IconSize.small }}
            tabIndex={disabled ? -1 : 0}
            onClick={onClearClick}
            onFocus={onClearFocus}
            onBlur={onClearBlur}
          />
        )}
        <Icon
          icon={IconActionArrowDropdownBottom}
          className={clsx('hse-Select__valueContainerIcon', {
            'hse-Select__valueContainerIcon_opened': isOpen
          })}
          size={IconSize.medium}
        />
      </div>
    );
  }
);
